/**
 * ONE FINANCING OPPORTUNITY: review it, offer terms, and later give the final approval.
 *
 * ## THREE DISTINCT ACTS, DELIBERATELY NOT COLLAPSED (section 38)
 *
 *   1. EXPRESS INTEREST   creates an offer. No loan, no ledger entry, no capital committed.
 *   2. THE BORROWER ACCEPTS   their act, not the financier's. It declines the rival offers.
 *   3. APPROVE FINANCING   the financier's final decision, and the ONLY thing that creates a loan.
 *
 * A design that let step 1 create a loan with a status would be simpler and would put a lender on the
 * hook for a facility it had merely enquired about. So this screen shows exactly which of the three
 * it is on, and never offers an action the request's state does not permit.
 *
 * ## DISBURSEMENT IS NOT APPROVAL
 *
 * After approval the loan exists and is `approved`. The money has not moved. Disbursement is a
 * separate action that runs the platform's compliance pipeline and can be refused, so the screen
 * keeps them apart rather than reporting a loan as funded because a form was submitted.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  LipaApiError,
  type CreditProduct,
  type CreditRequestDetail,
  type FinancerOffer,
} from '@/lib/api';
import { PageHeader, SectionTitle } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDateShort } from '@/lib/format';
import { formatWireAmount, sanitizeAmountTyping, toBaseUnits } from '@/lib/money';
import { newIdempotencyKey } from '@/lib/ids';

export default function CreditFinanceId() {
  const { id = '' } = useParams();

  const [request, setRequest] = useState<CreditRequestDetail | null>(null);
  const [products, setProducts] = useState<CreditProduct[]>([]);
  const [myOffer, setMyOffer] = useState<FinancerOffer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // The offer form.
  const [productId, setProductId] = useState('');
  const [amount, setAmount] = useState('');
  const [ratePct, setRatePct] = useState('');
  const [tenor, setTenor] = useState(24);
  const [conditions, setConditions] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const [detail, p, mine] = await Promise.all([
        api.financeMarket.opportunity(id).catch(() => null),
        api.creditMarket.products(),
        api.financeMarket.offers(),
      ]);
      setProducts(p.products);
      const offer = mine.offers.find((o) => o.request_id === id) ?? null;
      setMyOffer(offer);
      if (detail) {
        setRequest(detail);
        setAmount((a) => a || String(Number(detail.amount_minor) / 100));
        setTenor((t) => (t === 24 ? detail.tenor_months : t));
      }
      setProductId((v) => v || p.products[0]?.id || '');
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load this request.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!request && !myOffer) return <LoadingCard lines={5} />;

  const product = products.find((p) => p.id === productId);
  const maxFinancing = request?.max_financing_minor ?? '0';
  const requested = amount ? String(toBaseUnits(amount)) : '0';
  const overCeiling = BigInt(requested) > BigInt(maxFinancing || '0');

  const canOffer =
    request !== null &&
    ['open_for_financing', 'financing_interest_received'].includes(request.state) &&
    !myOffer;
  const canApprove = myOffer?.stage === 'awaiting_approval';
  const loanId = myOffer?.loan_id ?? null;
  const canDisburse = myOffer?.loan_state === 'approved';

  async function offer() {
    if (!product) return;
    setBusy(true);
    setError(null);
    try {
      await api.financeMarket.expressInterest(id, {
        product_id: product.id,
        amount,
        rate_bps: Math.round(Number(ratePct || product.rate_bps / 100) * 100),
        tenor_months: tenor,
        repayment_frequency: product.repayment_frequency,
        conditions: conditions
          .split('\n')
          .map((c) => c.trim())
          .filter(Boolean),
      });
      setNotice('Your offer has been sent. The borrower will compare it against the others.');
      await load();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not submit that offer.');
    } finally {
      setBusy(false);
    }
  }

  async function approve() {
    if (!myOffer) return;
    setBusy(true);
    setError(null);
    try {
      await api.financeMarket.approve(myOffer.id, 'Collateral, valuation and LTV confirmed.');
      setNotice('Approved. The loan now exists and is awaiting disbursement.');
      await load();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not approve this.');
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    if (!myOffer) return;
    setBusy(true);
    try {
      await api.financeMarket.decline(myOffer.id, 'Declined after final review.');
      setNotice('Declined. The request has gone back to the market for other financiers.');
      await load();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not decline this.');
    } finally {
      setBusy(false);
    }
  }

  async function disburse() {
    if (!loanId) return;
    setBusy(true);
    setError(null);
    try {
      await api.financeMarket.disburse(loanId, newIdempotencyKey());
      setNotice('Disbursed. The funds have reached the borrower and the loan is now active.');
      await load();
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not disburse this loan.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        title={request?.product_name ?? myOffer?.product_name ?? 'Financing opportunity'}
        subtitle={request?.reference ?? myOffer?.request_reference ?? ''}
        actions={
          myOffer ? <StatusPill status={myOffer.stage} /> : request ? <StatusPill status={request.state} /> : null
        }
      />

      {notice && (
        <div className="rounded-card border border-brand/30 bg-brand/5 p-3 text-sm text-ink">
          {notice}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-card border border-danger/40 bg-danger/5 p-3 text-sm text-ink"
        >
          {error}
        </div>
      )}

      {request && (
        <>
          <section className="rounded-card border border-border bg-surface p-4">
            <dl className="space-y-2">
              <Row label="Amount requested" value={`KES ${formatWireAmount(request.amount_minor)}`} />
              <Row label="Tenor" value={`${request.tenor_months} months`} />
              <Row label="Repayment" value={request.repayment_frequency} />
              <Row label="Purpose" value={request.purpose} />
              {request.financing_deadline && (
                <Row label="Financing closes" value={fmtDateShort(request.financing_deadline)} />
              )}
            </dl>
          </section>

          {/* THE COLLATERAL. What is securing this, what it is worth, and how far it stretches. */}
          <section>
            <SectionTitle>Collateral and valuation</SectionTitle>
            <ul className="space-y-2">
              {request.collateral.map((c) => (
                <li
                  key={c.id}
                  className="rounded-card border border-border bg-surface p-3.5 shadow-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{c.asset_name}</p>
                      <p className="text-nano text-ink-subtle">
                        {c.asset_class} · {(c.pledged_bps / 100).toFixed(2)}% pledged · lien{' '}
                        {c.lien_priority}
                      </p>
                    </div>
                    <StatusPill
                      status={c.state}
                      label={c.state === 'encumbered' ? 'Held by the Trust' : undefined}
                    />
                  </div>
                  <dl className="mt-2.5 grid grid-cols-3 gap-3 border-t border-border pt-2.5">
                    <Fact
                      label="Current value"
                      value={`KES ${formatWireAmount(c.valuation_minor)}`}
                    />
                    <Fact label="Haircut" value={`${c.haircut_bps / 100}%`} />
                    <Fact
                      label="Eligible"
                      value={`KES ${formatWireAmount(c.eligible_minor)}`}
                    />
                  </dl>
                </li>
              ))}
            </ul>
            <div className="mt-2.5 rounded-card bg-surface-sunken p-3">
              <dl className="space-y-1.5">
                <Row
                  label="Total eligible collateral"
                  value={`KES ${formatWireAmount(request.eligible_collateral_minor ?? '0')}`}
                />
                <Row
                  label="Maximum financing at the product ceiling"
                  value={`KES ${formatWireAmount(maxFinancing)}`}
                  accent
                />
              </dl>
              <p className="mt-2 text-nano leading-relaxed text-ink-subtle">
                Valuations are the current confirmed reference value, after the product&rsquo;s
                haircut. Any forward value shown elsewhere is an estimate and is not guaranteed.
              </p>
            </div>
          </section>
        </>
      )}

      {/* 1. EXPRESS INTEREST. */}
      {canOffer && product && (
        <Reveal>
          <section className="space-y-3.5 rounded-card border border-border bg-surface p-4">
            <p className="text-sm font-medium text-ink">Express interest</p>
            <p className="text-micro leading-relaxed text-ink-muted">
              This creates an offer with your terms. It commits no capital and creates no loan. The
              borrower compares it against the other offers and may accept or decline it.
            </p>

            <label className="block">
              <span className="text-micro font-medium text-ink">Product</span>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className="mt-1 w-full rounded-field border border-border bg-surface-sunken p-2 text-sm text-ink"
              >
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="text-micro font-medium text-ink">Amount (KES)</span>
                <input
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(sanitizeAmountTyping(e.target.value))}
                  className="tabular mt-1 w-full rounded-field border border-border bg-surface-sunken p-2 text-sm text-ink"
                />
              </label>
              <label className="block">
                <span className="text-micro font-medium text-ink">Rate (% p.a.)</span>
                <input
                  inputMode="decimal"
                  value={ratePct}
                  placeholder={(product.rate_bps / 100).toFixed(2)}
                  onChange={(e) => setRatePct(sanitizeAmountTyping(e.target.value))}
                  className="tabular mt-1 w-full rounded-field border border-border bg-surface-sunken p-2 text-sm text-ink"
                />
              </label>
              <label className="block">
                <span className="text-micro font-medium text-ink">Tenor (months)</span>
                <input
                  type="number"
                  min={1}
                  value={tenor}
                  onChange={(e) => setTenor(Number(e.target.value))}
                  className="tabular mt-1 w-full rounded-field border border-border bg-surface-sunken p-2 text-sm text-ink"
                />
              </label>
            </div>

            <label className="block">
              <span className="text-micro font-medium text-ink">Conditions</span>
              <span className="block text-nano text-ink-subtle">
                One per line. Shown to the borrower verbatim.
              </span>
              <textarea
                rows={3}
                value={conditions}
                onChange={(e) => setConditions(e.target.value)}
                placeholder="Fire and perils insurance to be assigned to the Trust."
                className="mt-1 w-full rounded-field border border-border bg-surface-sunken p-2.5 text-sm text-ink"
              />
            </label>

            {overCeiling && (
              <p className="rounded-field bg-danger/5 p-2.5 text-micro text-ink">
                That is more than the pledged collateral supports at this product&rsquo;s loan to
                value ceiling. The platform will refuse it.
              </p>
            )}

            <button
              type="button"
              disabled={busy || !amount || overCeiling}
              onClick={() => void offer()}
              className="w-full rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-40"
            >
              {busy ? 'Sending…' : 'Express interest'}
            </button>
          </section>
        </Reveal>
      )}

      {/* Where your offer stands. */}
      {myOffer && (
        <section className="rounded-card border border-border bg-surface p-4">
          <p className="text-sm font-medium text-ink">Your offer</p>
          <dl className="mt-2.5 space-y-1.5">
            <Row label="Amount" value={`KES ${formatWireAmount(myOffer.amount_minor)}`} />
            <Row label="Rate" value={`${(myOffer.rate_bps / 100).toFixed(2)}% p.a.`} />
            <Row label="Tenor" value={`${myOffer.tenor_months} months`} />
            <Row
              label="Total repayable"
              value={`KES ${formatWireAmount(myOffer.total_repayable_minor)}`}
              accent
            />
            <Row label="Status" value={myOffer.state} />
          </dl>
          {myOffer.state === 'submitted' && (
            <p className="mt-2.5 text-nano text-ink-subtle">
              Waiting for the borrower to compare the offers. Expires{' '}
              {fmtDateShort(myOffer.expires_at)}.
            </p>
          )}
        </section>
      )}

      {/* 3. FINAL APPROVAL. Only after the borrower has accepted. */}
      {canApprove && (
        <Reveal>
          <section className="rounded-card border border-brand/30 bg-brand/5 p-4">
            <p className="text-sm font-medium text-ink">The borrower has accepted your terms</p>
            <p className="mt-1.5 text-micro leading-relaxed text-ink-muted">
              Confirm the borrower, the collateral, the valuation, the loan to value and the Trust
              hold. Approving creates the loan and the lien; it does not move money.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void decline()}
                className="rounded-field border border-danger/40 px-3.5 py-2 text-sm font-medium text-danger disabled:opacity-40"
              >
                Reject financing
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void approve()}
                className="flex-1 rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                {busy ? 'Approving…' : 'Approve financing'}
              </button>
            </div>
          </section>
        </Reveal>
      )}

      {/* 4. DISBURSEMENT. A separate act, and a value movement. */}
      {canDisburse && loanId && (
        <Reveal>
          <section className="rounded-card border border-border bg-surface p-4">
            <p className="text-sm font-medium text-ink">Ready to disburse</p>
            <p className="mt-1.5 text-micro leading-relaxed text-ink-muted">
              The loan exists and the collateral is held. Disbursing moves the principal to the
              borrower&rsquo;s wallet through the platform&rsquo;s compliance pipeline, which can
              refuse it. The loan stays approved until the funds have actually settled.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void disburse()}
              className="mt-3 w-full rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {busy ? 'Disbursing…' : 'Disburse the loan'}
            </button>
          </section>
        </Reveal>
      )}

      <Link
        to="/credit/finance"
        className="inline-block rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink"
      >
        Back to Finance
      </Link>
    </div>
  );
}

function Row({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-micro text-ink-subtle">{label}</dt>
      <dd
        className={`tabular min-w-0 truncate text-right text-micro font-medium ${
          accent ? 'text-brand' : 'text-ink'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-nano text-ink-subtle">{label}</dt>
      <dd className="tabular truncate text-micro font-medium text-ink">{value}</dd>
    </div>
  );
}
